<template>
    <span class="position-relative">
        <span
            v-if="filterIsInitialized && appliedFilterValues.length > 0"
            class="position-absolute position-absolute top-75 start-75 translate-middle p-2 rounded-circle bg-success"
        ></span>
        <DropdownComp
            class="dropdown-center btn-subtle-hover p-0"
            icon="fa-filter"
            auto-close="outside"
            :show-arrow="false"
            button-type="Custom"
            @click="filterIsInitialized = true"
            :dropdown-items="dropdownItems"
        />
    </span>
</template>

<script setup lang="ts" generic="TRowData, TCellData">
import { computed, ref } from 'vue';
import { DateComp, DropdownComp } from '@/components';
import type { IGridCompExposed } from '../../Classes';
import type { IHeaderComponentParamsColumn } from '../../Classes/params/IHeaderComponentParamsColumn';
import type { IGridColumnFilter } from '../../Classes/IGridColumnFilter';
import { DropdownItemCustom, DropdownItemSeparator, DropdownItemTextInput } from '@/objects';
import ColumnFilterDropdownItem from './ColumnFilterDropdownItem.vue';
import { watchDebounced } from '@vueuse/core';
import type { Component } from 'vue';

const props = defineProps<{
    grid?: IGridCompExposed<TRowData>;
    column: IHeaderComponentParamsColumn;

    /**
     * The function we need to use to get the value from the data if the data is an object
     */
    valueGetter?: (data: TCellData) => string;
}>();

const filterIsInitialized = ref(false);

const searchText = ref<string>('');

const appliedFilterValues = ref<string[]>([]);

const thisColumnFilter: IGridColumnFilter = {
    columnId: props.column.colId,
    type: 'text',
    searchType: 'equals',
    values: [],
    valuegetter: props.valueGetter,
};

watchDebounced(
    appliedFilterValues,
    (value) => {
        thisColumnFilter.values = value;

        props.grid?.setFilter([thisColumnFilter]);
    },
    { debounce: 500 },
);

const getComponentValue = (item: any) => {
    if (item.renderer == DateComp) {
        return new Date(item.value as number);
    }

    return item.value;
};

const dropdownItems = computed(() => {
    if (!filterIsInitialized.value || !props.grid) {
        return [];
    }

    let gridFilterValues = props.grid?.getUniqueFilterColumnValues(props.column.colId) ?? [];

    if (searchText.value) {
        gridFilterValues = gridFilterValues.filter((g) => {
            const stringValue = props.valueGetter ? props.valueGetter(g.value) : g.value;
            return stringValue.toLowerCase().includes(searchText.value.toLowerCase());
        });
    }

    return [
        DropdownItemTextInput.create({
            modelValue: searchText,
            sticky: true,
        }),
        DropdownItemSeparator.create(),
        ...gridFilterValues.map((g) =>
            DropdownItemCustom.create({
                modelValue: appliedFilterValues,
                component: ColumnFilterDropdownItem as unknown as Component<any>, // Yuck. The component has generics, so vue gets confused
                componentProps: {
                    rendererProps: g.rendererParams,
                    value: getComponentValue(g),
                    renderer: g.renderer,
                },
            }),
        ),
    ];
});
</script>
